import {LitElement, html, unsafeCSS} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {queryAssignedElements} from 'lit/decorators/query-assigned-elements.js';
import cssStyles from "./bg-subnav.scss?inline"

@customElement('bg-subnav')
export class BgSubnav extends LitElement {
  @property({type: String}) label = '';
  @property({type: String}) ariaLabel = 'Page sub-navigation';
  @property({type: String}) hamburgerAriaLabel = 'Toggle page sub-navigation';
  @property({type: Boolean}) active = false;

  static styles = [
    unsafeCSS(cssStyles)
  ]

  @property({type: String, attribute: 'section-selector'})
  sectionSelector = '.bg-anchor';

  @queryAssignedElements({slot: 'links'})
  navLinks!: Array<HTMLElement>;

  private hamburger;

  firstUpdated() {
    // Get reference to hamburger
    this.hamburger = this.shadowRoot?.querySelector('bg-hamburger');
  }

  connectedCallback() {
    super.connectedCallback();
    // Wait for the next frame to ensure DOM is ready
    requestAnimationFrame(() => {
      this._setupEventListeners();
      this._setupIntersectionObserver();
    });
  }

  private _setupEventListeners() {
    // Listen for hamburger toggle events
    this.addEventListener('bg-hamburger-toggle', (e: Event) => {
      const customEvent = e as CustomEvent;
      
      if (customEvent.detail.targetId === 'subnav-links') {
        this.active = customEvent.detail.active;
      }
    });
    
    // Close menu when clicking anywhere
    document.addEventListener('click', () => {
        this.active = false;
        this.hamburger?.close();
    });
  }

  private _setupIntersectionObserver() {
    // Wait a bit longer to ensure all content is loaded
    setTimeout(() => {
      const sections = document.querySelectorAll(this.sectionSelector);

      if (!this.navLinks || this.navLinks.length === 0) {
        return;
      }

      const observerOptions = {
        root: null,
        // Adjust rootMargin to create a larger intersection area
        rootMargin: '-45% 0px -45% 0px',
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Remove active class from all links
            this.navLinks.forEach(link => {
              link.classList.remove('active');
              link.setAttribute('aria-current', 'false');
            });
            
            // Add active class to corresponding link
            const id = entry.target.id;
            const activeLink = this.navLinks.find(link => link.getAttribute('href') === `#${id}`);
            if (activeLink) {
              activeLink.classList.add('active');
              activeLink.setAttribute('aria-current', 'true');
            }
          }
        });
      }, observerOptions);

      // Observe all sections
      sections.forEach(section => {
        observer.observe(section);
      });
    }, 200); // Small delay to ensure DOM is ready
  }

  render() {
    return html`
      <nav class="subnav bg-color-scheme-dark" role="navigation" aria-label=${this.ariaLabel}>
        <div class="content">
          <div class="mobile-label">
            <span slot="mobile-label">${this.label}</span>
            <bg-hamburger aria-controls="subnav-links" aria-label=${this.hamburgerAriaLabel}></bg-hamburger>
          </div>
          <div 
            id="subnav-links" 
            class="links ${this.active ? 'active' : ''}" 
            role="menu"
            aria-hidden=${!this.active}
            aria-label="Page navigation links"
          >
            <div class="desktop-label">
              <span slot="label">${this.label}</span>
            </div>
            <slot name="links"></slot>
          </div>
        </div>
      </nav>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'bg-subnav': BgSubnav;
  }
} 